import React, { useCallback, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import _ from "lodash"
import {
  Box,
  Typography,
  IconButton,
  Slide,
  Button,
  useTheme,
  Paper,
  Avatar,
} from "@mui/material"

import { Trans } from "react-i18next"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import {
  PrevStepIcon,
  NextStepIcon,
  MarkerIcon,
  OriginMarkerIcon,
  PlayArrowIcon,
} from "components/icons"
import { StepProgressBar } from "components/Progress/StepProgressBar"
import { useStepByStep } from "hooks/direction/useStepByStep"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"
import { getIcon } from "utils/icon"
import { sanitizePublicPath } from "providers/venue/utils/fetchUtils"
import {
  PauseRoundedIcon,
  ReplayRoundedIcon,
} from "extensions/one-siam/components/icons"
import { VerticalDirectionImageDialog } from "extensions/one-siam/components/Dialog/VerticalStepImage/VerticalDirectionImageDialog"

export const StepByStepKioskVPage = () => {
  const [animateScroll, setAnimateScroll] = useState(false)
  const navigate = useNavigate()
  const { dataLoaded } = useVenue()

  const {
    steps,
    destinationParam,
    originParam,
    originLogo,
    originCategoryIconPath,
    originLevelName,
    destinationLevelName,
    currentStepNumber,
    currentStep,
    route,
    destinationLogo,
    destinationCategoryIconPath,
    currentVerticalStepMethod,
    // Auto play
    hasNextStep,
    hasPrevStep,
    isPlaying,
    goToNextStep,
    goToPrevStep,
    restartAutoPlay,
    resumeAutoPlay,
    pauseAutoPlay,
  } = useStepByStep()
  useStepByStepMap(route, currentStepNumber, {
    offset: { bottom: 411 },
    enabled: dataLoaded && steps.length > 0,
  })

  const { t } = useTranslation()

  const theme = useTheme()

  const {
    root,
    container,
    originIcon,
    destinationIcon,
    stepProgressBar,
    originIconContainer,
    destinationIconContainer,
    overviewProgressContainer,
    headerWrapper,
    cardContainer,
    cardBody,
    cardAction,
    actionButtonGroupContainer,
    descriptionContainer,
    descriptionText,
    exitButton,
    playerButton,
    iconNavButton,
    locationAvatar,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const { highlightedText } =
    theme.components[`DirectionStep`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )
  const handleClickStep = useCallback(
    (index) => {
      navigate(
        `/maps/dir/${originParam}/${destinationParam}/navigate/steps/${
          index + 1
        }`,
        { replace: true }
      )
    },
    [originParam, destinationParam, navigate]
  )

  const handleEnteredSlide = useCallback(() => {
    setAnimateScroll(true)
  }, [])

  const originIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const destinationIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.destination, destinationIcon) || (
        <MarkerIcon sx={destinationIcon} />
      ),
    [theme, destinationIcon]
  )

  const verticalDirectionImageUrl = useMemo(
    () =>
      sanitizePublicPath(
        _.get(
          theme?.assets?.verticalStep?.[currentVerticalStepMethod],
          "url",
          ""
        )
      ),
    [currentVerticalStepMethod, theme?.assets?.verticalStep]
  )

  return (
    <>
      <VerticalDirectionImageDialog
        imageSrc={verticalDirectionImageUrl}
        description={currentStep?.description}
        originLevelName={originLevelName}
        destinationLevelName={destinationLevelName}
      />

      <Box sx={root}>
        <Slide
          direction="up"
          in={dataLoaded && steps.length > 0}
          onEntered={handleEnteredSlide}
          mountOnEnter
          unmountOnExit
        >
          <Box sx={container}>
            <Paper sx={cardContainer}>
              <Box sx={cardBody}>
                <Box sx={headerWrapper}>
                  <Box sx={descriptionContainer}>
                    <Typography variant="caption" sx={descriptionText}>
                      <Trans
                        components={{
                          category: highlightedStepText,
                          destination: highlightedStepText,
                          landmark: highlightedStepText,
                        }}
                      >
                        {t(currentStep?.description.text, {
                          category: t(
                            currentStep?.description.options?.category
                          ),
                          destination: t(
                            currentStep?.description.options?.destination
                          ),
                          landmark: t(
                            currentStep?.description.options?.landmark
                          ),
                        })}
                      </Trans>
                    </Typography>
                  </Box>
                </Box>
                <Box sx={overviewProgressContainer}>
                  <Box sx={originIconContainer}>
                    <Avatar
                      variant="circular-logo"
                      sx={locationAvatar}
                      src={originLogo}
                    >
                      {getIcon(originCategoryIconPath) || originIconComp}
                    </Avatar>
                  </Box>
                  <StepProgressBar
                    steps={steps}
                    currentStep={currentStepNumber}
                    sx={stepProgressBar}
                    handleClickStep={handleClickStep}
                    enableAnimateScroll={animateScroll}
                  />
                  <Box
                    className={!hasNextStep ? "is-current-step" : ""}
                    sx={destinationIconContainer}
                  >
                    <Avatar
                      variant="circular-logo"
                      sx={locationAvatar}
                      src={destinationLogo}
                    >
                      {getIcon(destinationCategoryIconPath) ||
                        destinationIconComp}
                    </Avatar>
                  </Box>
                </Box>
              </Box>
              <Box sx={cardAction}>
                <IconButton
                  disabled={isPlaying || !hasPrevStep}
                  sx={iconNavButton}
                  onClick={goToPrevStep}
                >
                  <PrevStepIcon fontSize="inherit" />
                </IconButton>
                <Box sx={actionButtonGroupContainer}>
                  <Button
                    component={Link}
                    to="/maps"
                    sx={exitButton}
                    variant="outlined"
                    color="error"
                  >
                    {t("Exit")}
                  </Button>
                  {/* //TODO:: add condition to display palyer button */}
                  {isPlaying ? (
                    <Button
                      variant="contained"
                      sx={playerButton}
                      startIcon={<PauseRoundedIcon />}
                      onClick={pauseAutoPlay}
                    >
                      {t("stop")}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      sx={playerButton}
                      startIcon={
                        hasNextStep ? <PlayArrowIcon /> : <ReplayRoundedIcon />
                      }
                      onClick={hasNextStep ? resumeAutoPlay : restartAutoPlay}
                    >
                      {hasNextStep ? t("play") : t("play again")}
                    </Button>
                  )}
                </Box>
                <IconButton
                  disabled={isPlaying || !hasNextStep}
                  sx={iconNavButton}
                  onClick={goToNextStep}
                >
                  <NextStepIcon fontSize="inherit" />
                </IconButton>
              </Box>
            </Paper>
          </Box>
        </Slide>
      </Box>
    </>
  )
}
